import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance outside of component with interceptors
const createApiInstance = () => {
  const instance = axios.create({
    baseURL: API_URL
  });
  
  // Request interceptor: Always read fresh values from localStorage
  instance.interceptors.request.use(
    config => {
      // Get fresh token from localStorage
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      
      // Get fresh activeStructure from localStorage
      const savedStructure = localStorage.getItem('activeStructure');
      if (savedStructure) {
        try {
          const structure = JSON.parse(savedStructure);
          if (structure?.id) {
            config.headers['X-Active-Structure-Id'] = structure.id;
          }
        } catch (e) {
          // Invalid JSON, ignore
        }
      }
      
      return config;
    },
    error => Promise.reject(error)
  );
  
  return instance;
};

// Single instance with interceptors already attached
const api = createApiInstance();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  
  // Active structure for Super Admin impersonation
  const [activeStructure, setActiveStructure] = useState(() => {
    const saved = localStorage.getItem('activeStructure');
    return saved ? JSON.parse(saved) : null;
  });

  // Add response interceptor for automatic retry on structure-related 400 errors
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        
        // Only retry once and only for structure-related 400 errors
        if (
          error.response?.status === 400 &&
          error.response?.data?.detail?.includes('struttura') &&
          !originalRequest._retry &&
          activeStructure?.id
        ) {
          originalRequest._retry = true;
          // Ensure the structure header is set
          originalRequest.headers['X-Active-Structure-Id'] = activeStructure.id;
          // Wait a moment for state to stabilize
          await new Promise(resolve => setTimeout(resolve, 100));
          return api(originalRequest);
        }
        
        return Promise.reject(error);
      }
    );
    
    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [activeStructure]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      
      // Clear active structure if user is not super admin
      if (response.data.role !== 'super_admin' && activeStructure) {
        setActiveStructure(null);
        localStorage.removeItem('activeStructure');
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Refresh user data (useful after setup completion)
  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  // Heartbeat: keep session alive by pinging backend every 5 minutes
  useEffect(() => {
    if (!token || !user) return;

    const sendHeartbeat = async () => {
      try {
        await api.post('/auth/heartbeat');
      } catch (error) {
        // Silent fail - don't disrupt user experience
        console.debug('Heartbeat failed:', error?.message);
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Send heartbeat every 5 minutes (300000ms)
    const interval = setInterval(sendHeartbeat, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [token, user, api]);

  const login = async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    
    // Clear any active structure on new login
    setActiveStructure(null);
    localStorage.removeItem('activeStructure');
    
    return userData;
  };

  const logout = async () => {
    // Call logout endpoint to register session end
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout log error:', error);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('activeStructure');
    setToken(null);
    setUser(null);
    setActiveStructure(null);
  };

  // Enter a structure (Super Admin impersonation)
  const enterStructure = async (structure) => {
    if (user?.role !== 'super_admin') return;
    setActiveStructure(structure);
    localStorage.setItem('activeStructure', JSON.stringify(structure));
    
    // Log access
    try {
      await api.post('/auth/log-access', null, {
        params: {
          action: 'enter_structure',
          structure_id: structure.id,
          structure_name: structure.name
        }
      });
    } catch (error) {
      console.error('Failed to log access:', error);
    }
  };

  // Exit structure (return to Super Admin view)
  const exitStructure = async () => {
    const exitedStructure = activeStructure;
    setActiveStructure(null);
    localStorage.removeItem('activeStructure');
    
    // Log exit
    try {
      await api.post('/auth/log-access', null, {
        params: {
          action: 'exit_structure',
          structure_id: exitedStructure?.id,
          structure_name: exitedStructure?.name
        }
      });
    } catch (error) {
      console.error('Failed to log exit:', error);
    }
  };

  // Role checks
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdminStruttura = user?.role === 'admin_struttura';
  const isAdmin = isSuperAdmin || isAdminStruttura;
  const isKitchen = user?.role === 'cucina';
  const isOperator = user?.role === 'operatore';
  
  // Structure info - use active structure for Super Admin, or user's structure
  const structureId = activeStructure?.id || user?.structure_id;
  const structureName = activeStructure?.name || user?.structure_name;
  
  // Is Super Admin currently viewing a structure?
  const isViewingStructure = isSuperAdmin && !!activeStructure;
  
  // Effective role when Super Admin is viewing a structure
  const effectiveRole = isViewingStructure ? 'admin_struttura' : user?.role;
  
  // Permission checks (consider active structure for Super Admin)
  const canManageStructures = isSuperAdmin && !isViewingStructure;
  const canManageUsers = isSuperAdmin || isAdminStruttura || (user?.permissions?.users_edit === true);
  const canManageMenu = isSuperAdmin || isAdminStruttura || isKitchen; // cucina can manage menu
  const canEditMenu = isSuperAdmin || isAdminStruttura || isKitchen; // cucina can edit menu dishes, operatore only view
  const canManagePatients = isSuperAdmin || isAdminStruttura || isOperator; // cucina can only view
  const canEditPatients = isSuperAdmin || isAdminStruttura || isOperator; // cucina excluded
  // Discharge patients (libera camera): superadmin/admin always, others check permissions
  const canDischargePatients = isSuperAdmin || isAdminStruttura || (user?.permissions?.patients_discharge === true);
  // Archive view: superadmin/admin always, others check permissions
  const canViewArchive = isSuperAdmin || isAdminStruttura || (user?.permissions?.archive_view === true);
  const canTakeOrders = isSuperAdmin || isAdminStruttura || isOperator;
  // Orders edit: can create/modify orders in Comande page
  const canEditOrders = isSuperAdmin || isAdminStruttura || (user?.permissions?.orders_edit === true);
  // Patient sheets edit: can modify/delete orders from Cartellini page  
  const canEditPatientSheets = isSuperAdmin || isAdminStruttura || (user?.permissions?.patient_sheets_edit === true);
  const canViewKitchen = true;
  const canViewMenu = true; // everyone can view menu
  const canViewPatients = true; // everyone can view patients

  // Settings page permissions
  const canViewSettingsDishes = isSuperAdmin || isAdminStruttura || (user?.permissions?.settings_dishes_view === true);
  const canEditSettingsDishes = isSuperAdmin || isAdminStruttura || (user?.permissions?.settings_dishes_edit === true);
  const canViewSettingsCategories = isSuperAdmin || isAdminStruttura || (user?.permissions?.settings_categories_view === true);
  const canEditSettingsCategories = isSuperAdmin || isAdminStruttura || (user?.permissions?.settings_categories_edit === true);
  const canViewSettingsDiets = isSuperAdmin || isAdminStruttura || (user?.permissions?.settings_diets_view === true);
  const canEditSettingsDiets = isSuperAdmin || isAdminStruttura || (user?.permissions?.settings_diets_edit === true);
  const canViewSettingsAllergies = isSuperAdmin || isAdminStruttura || (user?.permissions?.settings_allergies_view === true);
  const canEditSettingsAllergies = isSuperAdmin || isAdminStruttura || (user?.permissions?.settings_allergies_edit === true);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      // Role checks
      isSuperAdmin,
      isAdminStruttura,
      isAdmin,
      isKitchen,
      isOperator,
      effectiveRole,
      // Structure info
      structureId,
      structureName,
      // Super Admin structure management
      activeStructure,
      isViewingStructure,
      enterStructure,
      exitStructure,
      // First login setup
      requiresSetup: user?.requires_setup || false,
      refreshUser,
      // Permission checks
      canManageStructures,
      canManageUsers,
      canManageMenu,
      canEditMenu,
      canManagePatients,
      canEditPatients,
      canDischargePatients,
      canViewArchive,
      canTakeOrders,
      canEditOrders,
      canEditPatientSheets,
      canViewKitchen,
      canViewMenu,
      canViewPatients,
      // Settings permissions
      canViewSettingsDishes,
      canEditSettingsDishes,
      canViewSettingsCategories,
      canEditSettingsCategories,
      canViewSettingsDiets,
      canEditSettingsDiets,
      canViewSettingsAllergies,
      canEditSettingsAllergies,
      api
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
